import { FormResponse, Investigation, Prescription, User } from '../types/api';
import apiService from '../services/api';

// Common styles for all PDF reports - Modern, Professional Design
const getCommonStyles = (accentColor: string = '#667eea') => `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    color: #1e293b;
    line-height: 1.7;
    padding: 40px;
    min-height: 100vh;
  }
  
  .report-container {
    max-width: 900px;
    margin: 0 auto;
    background: white;
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
  
  .header {
    background: linear-gradient(135deg, ${accentColor} 0%, #764ba2 100%);
    color: white;
    padding: 48px 40px;
    position: relative;
    overflow: hidden;
  }
  
  .header::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 60%;
    height: 200%;
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(15deg);
    pointer-events: none;
  }
  
  .header::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 40%;
    height: 150%;
    background: rgba(255, 255, 255, 0.05);
    transform: rotate(-10deg);
    pointer-events: none;
  }
  
  .brand {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
  }
  
  .brand-logo {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 24px;
    backdrop-filter: blur(8px);
  }
  
  .brand-text {
    display: flex;
    flex-direction: column;
  }
  
  .brand-name {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.5px;
  }
  
  .brand-tagline {
    font-size: 13px;
    opacity: 0.85;
    font-weight: 500;
  }
  
  .header h1 {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
    position: relative;
    z-index: 1;
  }
  
  .header-meta {
    display: flex;
    gap: 24px;
    margin-top: 16px;
    position: relative;
    z-index: 1;
  }
  
  .header-meta-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    opacity: 0.9;
  }
  
  .header-meta-icon {
    width: 32px;
    height: 32px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  
  .content {
    padding: 40px;
  }
  
  .summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
  }
  
  .summary-card {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    border: 1px solid #e2e8f0;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  
  .summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
  
  .summary-value {
    font-size: 36px;
    font-weight: 700;
    color: ${accentColor};
    line-height: 1.2;
    margin-bottom: 4px;
  }
  
  .summary-label {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .section {
    margin-bottom: 32px;
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }
  
  .section-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 20px 28px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .section-number {
    width: 32px;
    height: 32px;
    background: ${accentColor};
    color: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
  }
  
  .section-badge {
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    background: rgba(102, 126, 234, 0.1);
    color: ${accentColor};
  }
  
  .section-content {
    padding: 28px;
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }
  
  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .info-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .info-value {
    font-size: 15px;
    color: #334155;
    font-weight: 500;
  }
  
  .subsection {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px dashed #e2e8f0;
  }
  
  .subsection-title {
    font-size: 14px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .subsection-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }
  
  .item-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 12px;
    border-left: 4px solid ${accentColor};
    transition: background 0.2s;
  }
  
  .item-card:hover {
    background: #f1f5f9;
  }
  
  .item-card:last-child {
    margin-bottom: 0;
  }
  
  .item-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }
  
  .item-title {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
  }
  
  .item-meta {
    font-size: 12px;
    color: #64748b;
  }
  
  .item-content {
    font-size: 14px;
    color: #475569;
    line-height: 1.6;
  }
  
  .empty-state {
    text-align: center;
    padding: 32px 20px;
    color: #94a3b8;
    font-size: 14px;
    font-style: italic;
    background: #f8fafc;
    border-radius: 12px;
  }
  
  .score-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 700;
  }
  
  .medicine-badge {
    border-left-color: #ef4444;
  }
  
  .measurement-badge {
    border-left-color: #0ea5e9;
  }
  
  .investigation-badge {
    border-left-color: #f59e0b;
  }
  
  .tag {
    display: inline-flex;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    margin-right: 6px;
    margin-bottom: 6px;
  }
  
  .tag-primary {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
  }
  
  .tag-success {
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
  }
  
  .tag-warning {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706;
  }
  
  .tag-danger {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
  
  .footer {
    background: #f8fafc;
    padding: 24px 40px;
    border-top: 1px solid #e2e8f0;
    text-align: center;
  }
  
  .footer-text {
    font-size: 12px;
    color: #94a3b8;
  }
  
  .footer-brand {
    font-weight: 600;
    color: ${accentColor};
  }
  
  @media print {
    body {
      background: white;
      padding: 0;
    }
    
    .report-container {
      box-shadow: none;
      border-radius: 0;
    }
    
    .section {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    
    .header {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Download Form Responses as PDF with detailed questions and answers
export const downloadFormResponsesAsPDF = async (responses: FormResponse[], filename: string = 'form-responses') => {
  if (!responses || responses.length === 0) {
    alert('No data to download');
    return;
  }

  try {
    // Get user data with error handling
    let user: User | null = null;
    try {
      user = await getUserById(responses[0].sbu_id);
    } catch (error) {
      console.warn('Failed to fetch user data:', error);
      // Continue without user data
    }

    const avgScore = responses.length > 0 
      ? (responses.reduce((sum, r) => sum + (r.score || 0), 0) / responses.length).toFixed(1) 
      : 'N/A';

    // Create detailed PDF content as HTML
    const pdfContent = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Form Responses Report - Swasth Bharat</title>
          <style>${getCommonStyles('#667eea')}</style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <div class="brand">
                <div class="brand-logo">SB</div>
                <div class="brand-text">
                  <span class="brand-name">Swasth Bharat</span>
                  <span class="brand-tagline">Healthcare Management System</span>
                </div>
              </div>
              <h1>📋 Form Responses Report</h1>
              <div class="header-meta">
                <div class="header-meta-item">
                  <div class="header-meta-icon">📅</div>
                  <span>Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div class="header-meta-item">
                  <div class="header-meta-icon">📊</div>
                  <span>${responses.length} Response${responses.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            <div class="content">
              <div class="summary">
                <div class="summary-card">
                  <div class="summary-value">${responses.length}</div>
                  <div class="summary-label">Total Responses</div>
                </div>
                <div class="summary-card">
                  <div class="summary-value">${avgScore}</div>
                  <div class="summary-label">Average Score</div>
                </div>
                <div class="summary-card">
                  <div class="summary-value">${responses.filter(r => r.score !== undefined && r.score !== null).length}</div>
                  <div class="summary-label">Scored Responses</div>
                </div>
                <div class="summary-card">
                  <div class="summary-value">${responses.reduce((sum, r) => sum + (r.answers?.length || 0), 0)}</div>
                  <div class="summary-label">Total Answers</div>
                </div>
              </div>

              ${responses.map((response, index) => `
                <div class="section">
                  <div class="section-header">
                    <div class="section-title">
                      <div class="section-number">${index + 1}</div>
                      Response #${index + 1}
                    </div>
                    ${response.score !== undefined && response.score !== null 
                      ? `<div class="score-badge">⭐ Score: ${response.score}</div>` 
                      : '<div class="section-badge">Unscored</div>'
                    }
                  </div>
                  
                  <div class="section-content">
                    <div class="info-grid">
                      <div class="info-item">
                        <span class="info-label">Response ID</span>
                        <span class="info-value">${response._id || 'N/A'}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">Form ID</span>
                        <span class="info-value">${response.form_id || 'N/A'}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">User ID</span>
                        <span class="info-value">${response.sbu_id || 'N/A'}</span>
                      </div>
                      ${user ? `
                        <div class="info-item">
                          <span class="info-label">User Name</span>
                          <span class="info-value">${user.first_name} ${user.middle_name || ''} ${user.last_name}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">User Email</span>
                          <span class="info-value">${user.email}</span>
                        </div>
                      ` : ''}
                      <div class="info-item">
                        <span class="info-label">Submitted On</span>
                        <span class="info-value">${response.createdAt ? new Date(response.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</span>
                      </div>
                    </div>

                    <div class="subsection">
                      <div class="subsection-title">
                        <span class="subsection-icon" style="background: rgba(102, 126, 234, 0.1); color: #667eea;">❓</span>
                        Questions & Answers
                      </div>
                      ${response.answers && response.answers.length > 0 ? 
                        response.answers.map((answer, answerIndex) => `
                          <div class="item-card">
                            <div class="item-header">
                              <span class="item-title">Q${answerIndex + 1}. ${answer.question || 'N/A'}</span>
                              <span class="tag tag-primary">${answer.answer_type || 'text'}</span>
                            </div>
                            <div class="item-content">
                              <strong>Answer:</strong> ${answer.answer_text || 'No answer provided'}
                            </div>
                            ${answer.interpretation && answer.interpretation.trim() !== '' ? `
                              <div class="item-content" style="margin-top: 8px; padding: 8px 12px; background: linear-gradient(135deg, #fef3c7, #fffbeb); border-radius: 6px; border-left: 3px solid #f59e0b;">
                                <span style="color: #f59e0b; margin-right: 6px;">💡</span>
                                <strong style="color: #92400e;">Interpretation:</strong> 
                                <span style="color: #92400e;">${answer.interpretation}</span>
                              </div>
                            ` : ''}
                          </div>
                        `).join('') : 
                        '<div class="empty-state">📭 No answers provided for this response</div>'
                      }
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>

            <div class="footer">
              <p class="footer-text">
                This report was generated by <span class="footer-brand">Swasth Bharat</span> Healthcare Management System
              </p>
            </div>
          </div>
        </body>
      </html>
    `;

    // Create and download file
    const blob = new Blob([pdfContent], { type: 'text/html' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error generating download:', error);
    alert('Failed to generate download. Please try again.');
    throw error;
  }
};

async function getUserById(sbu_id: string) {
    try {
        const user = await apiService.getUserById(sbu_id);
        return user;
    } catch (error) {
        console.error('Error fetching user by ID:', error);
        throw error;
    }
}

// Download Investigations as PDF
export const downloadInvestigationsAsPDF = async (investigations: Investigation[], filename: string = 'investigations') => {
  if (!investigations || investigations.length === 0) {
    alert('No data to download');
    return;
  }

  // Create detailed PDF content as HTML
  const pdfContent = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Investigations Report - Swasth Bharat</title>
        <style>${getCommonStyles('#10b981')}</style>
      </head>
      <body>
        <div class="report-container">
          <div class="header">
            <div class="brand">
              <div class="brand-logo">SB</div>
              <div class="brand-text">
                <span class="brand-name">Swasth Bharat</span>
                <span class="brand-tagline">Healthcare Management System</span>
              </div>
            </div>
            <h1>🔬 Investigations Report</h1>
            <div class="header-meta">
              <div class="header-meta-item">
                <div class="header-meta-icon">📅</div>
                <span>Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div class="header-meta-item">
                <div class="header-meta-icon">🔬</div>
                <span>${investigations.length} Investigation${investigations.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          <div class="content">
            <div class="summary">
              <div class="summary-card">
                <div class="summary-value">${investigations.length}</div>
                <div class="summary-label">Total Investigations</div>
              </div>
              <div class="summary-card">
                <div class="summary-value">${investigations.filter(i => i.questions && i.questions.length > 0).length}</div>
                <div class="summary-label">With Questions</div>
              </div>
              <div class="summary-card">
                <div class="summary-value">${investigations.reduce((sum, i) => sum + (i.questions?.length || 0), 0)}</div>
                <div class="summary-label">Total Questions</div>
              </div>
            </div>

            ${investigations.map((investigation, index) => `
              <div class="section">
                <div class="section-header">
                  <div class="section-title">
                    <div class="section-number">${index + 1}</div>
                    ${investigation.investigation_name || 'Unnamed Investigation'}
                  </div>
                  <div class="section-badge">${investigation.questions?.length || 0} Questions</div>
                </div>
                
                <div class="section-content">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Investigation ID</span>
                      <span class="info-value">${investigation.iv_id || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Doctor ID</span>
                      <span class="info-value">${investigation.sbd_id || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Prescription ID</span>
                      <span class="info-value">${investigation.prescription_id || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Created On</span>
                      <span class="info-value">${investigation.createdAt ? new Date(investigation.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Last Updated</span>
                      <span class="info-value">${investigation.updatedAt ? new Date(investigation.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                    </div>
                  </div>

                  <div class="subsection">
                    <div class="subsection-title">
                      <span class="subsection-icon" style="background: rgba(16, 185, 129, 0.1); color: #10b981;">📝</span>
                      Investigation Questions
                    </div>
                    ${investigation.questions && investigation.questions.length > 0 ? 
                      investigation.questions.map((question, questionIndex) => `
                        <div class="item-card">
                          <div class="item-header">
                            <span class="item-title">Q${questionIndex + 1}. ${question.questionText || question.question || 'N/A'}</span>
                            <span class="tag ${question.isRequired ? 'tag-danger' : 'tag-success'}">${question.isRequired ? 'Required' : 'Optional'}</span>
                          </div>
                          <div class="item-content">
                            <span class="tag tag-primary">${question.answer_type || 'text'}</span>
                            ${question.score ? `<span class="tag tag-warning">Score: ${question.score}</span>` : ''}
                          </div>
                        </div>
                      `).join('') : 
                      '<div class="empty-state">📭 No questions defined for this investigation</div>'
                    }
                  </div>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="footer">
            <p class="footer-text">
              This report was generated by <span class="footer-brand">Swasth Bharat</span> Healthcare Management System
            </p>
          </div>
        </div>
      </body>
    </html>
  `;

  // Create and download file
  const blob = new Blob([pdfContent], { type: 'text/html' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.html`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
};

// Download Prescriptions as PDF
export const downloadPrescriptionsAsPDF = async (prescriptions: Prescription[], filename: string = 'prescriptions') => {
  if (!prescriptions || prescriptions.length === 0) {
    alert('No data to download');
    return;
  }

  // Create detailed PDF content as HTML
  const pdfContent = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Prescriptions Report - Swasth Bharat</title>
        <style>${getCommonStyles('#ef4444')}</style>
      </head>
      <body>
        <div class="report-container">
          <div class="header">
            <div class="brand">
              <div class="brand-logo">SB</div>
              <div class="brand-text">
                <span class="brand-name">Swasth Bharat</span>
                <span class="brand-tagline">Healthcare Management System</span>
              </div>
            </div>
            <h1>💊 Prescriptions Report</h1>
            <div class="header-meta">
              <div class="header-meta-item">
                <div class="header-meta-icon">📅</div>
                <span>Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div class="header-meta-item">
                <div class="header-meta-icon">💊</div>
                <span>${prescriptions.length} Prescription${prescriptions.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          <div class="content">
            <div class="summary">
              <div class="summary-card">
                <div class="summary-value">${prescriptions.length}</div>
                <div class="summary-label">Total Prescriptions</div>
              </div>
              <div class="summary-card">
                <div class="summary-value">${prescriptions.reduce((sum, p) => sum + (p.medicine?.length || 0), 0)}</div>
                <div class="summary-label">Total Medicines</div>
              </div>
              <div class="summary-card">
                <div class="summary-value">${prescriptions.reduce((sum, p) => sum + (p.measurements?.length || 0), 0)}</div>
                <div class="summary-label">Measurements</div>
              </div>
              <div class="summary-card">
                <div class="summary-value">${prescriptions.reduce((sum, p) => sum + (p.investigation?.length || 0), 0)}</div>
                <div class="summary-label">Investigations</div>
              </div>
            </div>

            ${prescriptions.map((prescription, index) => `
              <div class="section">
                <div class="section-header">
                  <div class="section-title">
                    <div class="section-number">${index + 1}</div>
                    Prescription #${prescription.prescription_id || index + 1}
                  </div>
                  <div class="section-badge">${prescription.date ? new Date(prescription.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No Date'}</div>
                </div>
                
                <div class="section-content">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Prescription ID</span>
                      <span class="info-value">${prescription.prescription_id || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Patient ID</span>
                      <span class="info-value">${prescription.sbu_id || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Doctor ID</span>
                      <span class="info-value">${prescription.sbd_id || 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Prescription Date</span>
                      <span class="info-value">${prescription.date ? new Date(prescription.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Next Visit Date</span>
                      <span class="info-value">${prescription.next_date ? new Date(prescription.next_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not scheduled'}</span>
                    </div>
                  </div>

                  <!-- Patient Condition -->
                  <div class="subsection">
                    <div class="subsection-title">
                      <span class="subsection-icon" style="background: rgba(239, 68, 68, 0.1); color: #ef4444;">🩺</span>
                      Patient Condition
                    </div>
                    <div class="info-grid">
                      <div class="info-item" style="grid-column: 1 / -1;">
                        <span class="info-label">Chief Complaints</span>
                        <span class="info-value">${prescription.complains || 'No complaints recorded'}</span>
                      </div>
                      <div class="info-item" style="grid-column: 1 / -1;">
                        <span class="info-label">Diagnosis</span>
                        <span class="info-value">
                          ${prescription.diagnosis && prescription.diagnosis.length > 0 
                            ? prescription.diagnosis.map(d => `
                              <div style="display: inline-flex; flex-direction: column; margin-right: 12px; margin-bottom: 8px; padding: 8px 14px; background: rgba(239, 68, 68, 0.06); border-radius: 10px; border: 1px solid rgba(239, 68, 68, 0.15);">
                                <span class="tag tag-danger" style="margin: 0 0 4px 0;">${d.name}${d.snomed_code ? ` <span style="font-size: 10px; opacity: 0.7;">(${d.snomed_code})</span>` : ''}</span>
                                ${d.since ? `<span style="font-size: 11px; color: #64748b;">Since: ${d.since}</span>` : ''}
                                ${d.remarks ? `<span style="font-size: 11px; color: #64748b;">${d.remarks}</span>` : ''}
                              </div>
                            `).join('') 
                            : 'No diagnosis recorded'}
                        </span>
                      </div>
                      <div class="info-item" style="grid-column: 1 / -1;">
                        <span class="info-label">Medical Advice</span>
                        <span class="info-value">${prescription.advice || 'No advice recorded'}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Medicines -->
                  <div class="subsection">
                    <div class="subsection-title">
                      <span class="subsection-icon" style="background: rgba(239, 68, 68, 0.1); color: #ef4444;">💊</span>
                      Prescribed Medicines
                    </div>
                    ${prescription.medicine && prescription.medicine.length > 0 ? 
                      prescription.medicine.map((medicine, medicineIndex) => `
                        <div class="item-card medicine-badge">
                          <div class="item-header">
                            <span class="item-title">${medicineIndex + 1}. ${medicine.name || 'Unnamed Medicine'}</span>
                            ${medicine.administration ? `<span class="tag tag-primary">${medicine.administration}</span>` : ''}
                          </div>
                          <div class="item-content">
                            <strong>Dosage:</strong> ${medicine.dosage || 'Not specified'} &nbsp;•&nbsp; 
                            <strong>Timings:</strong> ${medicine.timings || 'Not specified'}
                            ${medicine.when ? ` &nbsp;•&nbsp; <strong>When:</strong> ${medicine.when}` : ''}
                            ${medicine.duration ? ` &nbsp;•&nbsp; <strong>Duration:</strong> ${medicine.duration}` : ''}
                          </div>
                          ${medicine.comments ? `<div class="item-content" style="margin-top: 6px; font-style: italic; color: #64748b;">${medicine.comments}</div>` : ''}
                        </div>
                      `).join('') : 
                      '<div class="empty-state">💊 No medicines prescribed</div>'
                    }
                  </div>

                  <!-- Measurements -->
                  <div class="subsection">
                    <div class="subsection-title">
                      <span class="subsection-icon" style="background: rgba(14, 165, 233, 0.1); color: #0ea5e9;">📊</span>
                      Vital Measurements
                    </div>
                    ${prescription.measurements && prescription.measurements.length > 0 ? 
                      `<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px;">
                        ${prescription.measurements.map((measurement) => `
                          <div class="item-card measurement-badge" style="margin-bottom: 0;">
                            <div class="item-title">${measurement.name || 'Measurement'}</div>
                            <div class="item-content" style="font-size: 20px; font-weight: 700; color: #0ea5e9;">
                              ${measurement.value || 'N/A'} <span style="font-size: 14px; font-weight: 500; color: #64748b;">${measurement.unit || ''}</span>
                            </div>
                          </div>
                        `).join('')}
                      </div>` : 
                      '<div class="empty-state">📊 No measurements recorded</div>'
                    }
                  </div>

                  <!-- Investigations -->
                  <div class="subsection">
                    <div class="subsection-title">
                      <span class="subsection-icon" style="background: rgba(245, 158, 11, 0.1); color: #f59e0b;">🔬</span>
                      Ordered Investigations
                    </div>
                    ${prescription.investigation && prescription.investigation.length > 0 ? 
                      prescription.investigation.map((investigation, investigationIndex) => `
                        <div class="item-card investigation-badge">
                          <div class="item-header">
                            <span class="item-title">${investigationIndex + 1}. ${investigation.name || 'Unnamed Investigation'}</span>
                            <span class="tag tag-warning">${investigation.iv_id || 'No ID'}</span>
                          </div>
                        </div>
                      `).join('') : 
                      '<div class="empty-state">🔬 No investigations ordered</div>'
                    }
                  </div>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="footer">
            <p class="footer-text">
              This report was generated by <span class="footer-brand">Swasth Bharat</span> Healthcare Management System
            </p>
          </div>
        </div>
      </body>
    </html>
  `;

  // Create and download file
  const blob = new Blob([pdfContent], { type: 'text/html' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.html`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
};
